import React, { useState, useEffect } from 'react';
import { X, MapPin, User, Calendar, Phone, Save } from 'lucide-react';
import { Town } from '../../types';
import { darkModeClasses } from '../../utils/darkMode';

interface EditTownModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (town: Town) => void;
  town: Town | null;
}

export const EditTownModal: React.FC<EditTownModalProps> = ({
  isOpen,
  onClose,
  onSave,
  town
}) => {
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    contactPerson: '',
    setupDate: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (town) {
      setFormData({
        name: town.name,
        location: town.location,
        contactPerson: town.contactPerson,
        setupDate: town.setupDate
      });
    }
  }, [town]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Town name is required';
    }
    
    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }
    
    if (!formData.contactPerson.trim()) {
      newErrors.contactPerson = 'Contact person is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || !town) return;
    
    const updatedTown: Town = {
      ...town,
      ...formData
    };
    
    onSave(updatedTown);
    onClose();
    setErrors({});
  };

  if (!isOpen || !town) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto ${darkModeClasses.container}`}>
        <div className={`flex items-center justify-between p-6 border-b ${darkModeClasses.border}`}>
          <h2 className={`text-xl font-semibold flex items-center ${darkModeClasses.textPrimary}`}>
            <MapPin className="h-6 w-6 mr-2 text-blue-600" />
            Edit Town
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className={`block text-sm font-medium mb-2 ${darkModeClasses.textSecondary}`}>
              Town Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className={`w-full rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.name ? 'border-red-500' : darkModeClasses.input
              }`}
              placeholder="e.g., ACCRA, KUMASI"
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${darkModeClasses.textSecondary}`}>
              Location *
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                className={`w-full rounded-lg pl-10 pr-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.location ? 'border-red-500' : darkModeClasses.input
                }`}
                placeholder="e.g., Greater Accra Region"
              />
            </div>
            {errors.location && <p className="text-red-500 text-sm mt-1">{errors.location}</p>}
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${darkModeClasses.textSecondary}`}>
              Contact Person *
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={formData.contactPerson}
                onChange={(e) => setFormData({...formData, contactPerson: e.target.value})}
                className={`w-full rounded-lg pl-10 pr-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.contactPerson ? 'border-red-500' : darkModeClasses.input
                }`}
                placeholder="Full name of contact person"
              />
            </div>
            {errors.contactPerson && <p className="text-red-500 text-sm mt-1">{errors.contactPerson}</p>}
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${darkModeClasses.textSecondary}`}>
              Setup Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="date"
                value={formData.setupDate}
                onChange={(e) => setFormData({...formData, setupDate: e.target.value})}
                className={`w-full rounded-lg pl-10 pr-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkModeClasses.input}`}
              />
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-blue-600">Members:</span>
                <span className="font-medium text-blue-900 ml-1">{town.memberCount || 0}</span>
              </div>
              <div>
                <span className="text-blue-600">Revenue:</span>
                <span className="font-medium text-blue-900 ml-1">GHS {(town.totalRevenue || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 py-2 px-4 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <Save className="h-4 w-4" />
              <span>Save Changes</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};